import Footer from "@/components/app-shell/Footer";
import Header from "@/components/app-shell/Header";
import ResizablePane from "@/components/app-shell/ResizablePane";
import Library, { LibraryDragHandle } from "@/components/library/Library";
import DragPreviewOverlay, { ClientRectData, DragPreviewInitPayload } from "@/components/library/LibraryDragPreview";
import BeginnerTourStartModal from "@/components/modals/user/BeginnerTutorialStart";
import Timeline from "@/components/timeline/Timeline";
import { AuthContext } from "@/context/AuthContext";
import { DeviceLayout, useDeviceLayout } from "@/hooks/useDeviceLayout";
import { TimelineSectionDropCompletedEvent } from "@/javascript/custom-events";
import { Section } from "@/javascript/music-objects";
import { DndContext, DragCancelEvent, DragEndEvent, DragMoveEvent, DragOverEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { AppShell } from "@mantine/core";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import LoadingScreen from "./LoadingScreen"; // Adjust the path as necessary

interface ProjectPageProps {
    loadingInstrument: boolean
};

const ProjectPage: React.FC<ProjectPageProps> = ({ loadingInstrument }) => {
    const [draggingLibrarySections, setDraggingLibrarySections] = useState<Section[] | null>(null);
    const [dragPreviewData, setDragPreviewData] = useState<DragPreviewInitPayload | null>(null);
    const [dragCursor, setDragCursor] = useState<{ x: number; y: number } | null>(null);
    const [dragOverTimeline, setDragOverTimeline] = useState(false);
    const [shouldReturnPreview, setShouldReturnPreview] = useState(false);
    const [dragDropCompleted, setDragDropCompleted] = useState(false);
    const [ghostLayouts, setGhostLayouts] = useState<Record<string, ClientRectData>>({});
    const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);
    const [loadingPercent, setLoadingPercent] = useState(0);
    const dragStartCursorRef = useRef<{ x: number; y: number } | null>(null);

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("Project Page must be a child of AuthContext.");
    }
    const { updateProject, project, user, beginnerTourStartModalOpen, setBeginnerTourStartModalOpen, startBeginnerTour, loadingPercent: authLoadingPercent, loading, updatePreTourChecklist, logout, beginnerTourActive, currentTourStep, moveToNextTourStep, moveToPrevTourStep, jumpToTourStep } = auth;

    const libraryRef = useRef<LibraryDragHandle>(null);
    const timelineRef = useRef<Timeline | null>(null);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                // Small movement threshold:
                // - Click: pointer up before moving this far -> no drag
                // - Drag: move more than this distance -> drag starts immediately
                distance: 5,
            },
        })
    );

    const extractClientCoordinates = useCallback((event?: Event) => {
        if (!event) return null;

        const touchEvent = event as TouchEvent;
        if ('touches' in touchEvent && touchEvent.touches.length > 0) {
            const touch = touchEvent.touches[0];
            return { x: touch.clientX, y: touch.clientY };
        }

        const changedTouches = (event as TouchEvent).changedTouches;
        if (changedTouches && changedTouches.length > 0) {
            const touch = changedTouches[0];
            return { x: touch.clientX, y: touch.clientY };
        }

        const pointerEvent = event as PointerEvent;
        if (typeof pointerEvent.clientX === "number" && typeof pointerEvent.clientY === "number") {
            return { x: pointerEvent.clientX, y: pointerEvent.clientY };
        }

        const mouseEvent = event as MouseEvent;
        if (typeof mouseEvent.clientX === "number" && typeof mouseEvent.clientY === "number") {
            return { x: mouseEvent.clientX, y: mouseEvent.clientY };
        }

        return null;
    }, []);

    const updateCursorFromDelta = useCallback((event: DragMoveEvent | DragOverEvent) => {
        const base = dragStartCursorRef.current ?? dragPreviewData?.initialCursor;
        if (!base) {
            return null;
        }
        const nextCursor = {
            x: base.x + event.delta.x,
            y: base.y + event.delta.y,
        };
        setDragCursor(nextCursor);
        return nextCursor;
    }, [dragPreviewData]);

    const handleDragStartFromDnd = useCallback((event: DragStartEvent) => {
        const data = event.active.data.current as { type?: string; sectionId?: string } | undefined;
        if (!data) {
            return;
        }

        if (data.type === "timeline-item") {
            const pointer = extractClientCoordinates(event.activatorEvent) ?? null;
            dragStartCursorRef.current = pointer;
            setDragCursor(pointer);
            return;
        }

        if (data.type !== "library-section" || !data.sectionId) {
            return;
        }

        const pointer = extractClientCoordinates(event.activatorEvent) ?? null;
        dragStartCursorRef.current = pointer;
        const prepared = libraryRef.current?.prepareDragStart(data.sectionId, pointer);
        if (!prepared) {
            return;
        }

        setDraggingLibrarySections(prepared.draggingSections);
        if (prepared.previewPayload) {
            setDragPreviewData(prepared.previewPayload);
            setDragCursor(prepared.previewPayload.initialCursor);
        }
        setDragOverTimeline(false);
        setShouldReturnPreview(false);
        setDragDropCompleted(false);
        setGhostLayouts({});
    }, [extractClientCoordinates]);

    const handleDragMoveFromDnd = useCallback((event: DragMoveEvent) => {
        const data = event.active.data.current as { type?: string } | undefined;
        if (!data) {
            return;
        }

        if (data.type === "timeline-item") {
            const base = dragStartCursorRef.current ?? extractClientCoordinates(event.activatorEvent);
            if (!base || !timelineRef.current) {
                return;
            }
            const nextCursor = {
                x: base.x + event.delta.x,
                y: base.y + event.delta.y,
            };
            setDragCursor(nextCursor);
            const timelineLeft = timelineRef.current.getTimelineBoundingLeft();
            timelineRef.current.executeItemAction(nextCursor.x, timelineLeft, false);
            return;
        }

        if (data.type !== "library-section") {
            return;
        }
        const nextCursor = updateCursorFromDelta(event) ?? extractClientCoordinates(event.activatorEvent);
        if (!nextCursor) {
            return;
        }
        const overTimeline = timelineRef.current?.isPointWithinTimeline(nextCursor.x, nextCursor.y) ?? false;
        setDragOverTimeline(overTimeline);
        if (overTimeline && draggingLibrarySections && timelineRef.current) {
            timelineRef.current.previewLibrarySectionDropAtClientX(nextCursor.x);
        }
    }, [draggingLibrarySections, extractClientCoordinates, updateCursorFromDelta]);

    const handleDragOverFromDnd = useCallback((event: DragOverEvent) => {
        handleDragMoveFromDnd(event);
    }, [handleDragMoveFromDnd]);

    const finalizeDragSession = useCallback((event: DragEndEvent | DragCancelEvent, wasCanceled: boolean) => {
        const data = event.active.data.current as { type?: string } | undefined;
        if (!data) {
            return;
        }

        if (data.type === "timeline-item") {
            const base = dragStartCursorRef.current ?? extractClientCoordinates(event.activatorEvent);
            if (!base || !timelineRef.current) {
                return;
            }
            const finalCursor = {
                x: base.x + event.delta.x,
                y: base.y + event.delta.y,
            };
            const timelineLeft = timelineRef.current.getTimelineBoundingLeft();
            timelineRef.current.executeItemAction(finalCursor.x, timelineLeft, true);
            dragStartCursorRef.current = null;
            setDragCursor(null);
            return;
        }

        if (data.type !== "library-section") {
            return;
        }

        const finalCursor =
            dragCursor ??
            (dragPreviewData
                ? {
                    x: dragPreviewData.initialCursor.x + event.delta.x,
                    y: dragPreviewData.initialCursor.y + event.delta.y,
                }
                : null);

        const droppedOnTimeline = !wasCanceled && Boolean(
            finalCursor && timelineRef.current?.isPointWithinTimeline(finalCursor.x, finalCursor.y)
        );

        if (droppedOnTimeline) {
            timelineRef.current?.commitLibrarySectionDrop();
            setDragDropCompleted(true);
            setDragOverTimeline(true);
        } else {
            timelineRef.current?.cancelLibrarySectionPreview();
            setShouldReturnPreview(true);
            setDragOverTimeline(false);
            setDraggingLibrarySections(null);
            setGhostLayouts({});
        }

        libraryRef.current?.endDrag();
        dragStartCursorRef.current = null;
    }, [dragCursor, dragPreviewData, extractClientCoordinates]);

    const handleDragEndFromDnd = useCallback((event: DragEndEvent) => finalizeDragSession(event, false), [finalizeDragSession]);
    const handleDragCancelFromDnd = useCallback((event: DragCancelEvent) => finalizeDragSession(event, true), [finalizeDragSession]);

    const handlePreviewAnimationComplete = useCallback(() => {
        setDragPreviewData(null);
        setDragCursor(null);
        setDragOverTimeline(false);
        setShouldReturnPreview(false);
        setDragDropCompleted(false);
        setGhostLayouts({});
        dragStartCursorRef.current = null;
    }, []);

    const handleGhostLayoutChange = useCallback((layouts: { matchId: string; rect: ClientRectData }[]) => {
        if (!layouts.length) {
            setGhostLayouts({});
            return;
        }
        const nextLayouts: Record<string, ClientRectData> = {};
        layouts.forEach(({ matchId, rect }) => {
            nextLayouts[matchId] = rect;
        });
        setGhostLayouts(nextLayouts);
    }, []);

    useEffect(() => {
        if (showLoadingScreen) {
            setLoadingPercent(authLoadingPercent);
        } else {
            updatePreTourChecklist("loading-screen-done", true);
        }
    }, [authLoadingPercent, showLoadingScreen, updatePreTourChecklist]);

    useEffect(() => {
        if (loading) {
            setShowLoadingScreen(true);
            setLoadingPercent(0);
        }
    }, [loading]);

    useEffect(() => {
        const handleDropComplete = () => {
            setDragDropCompleted(true);
        };
        TimelineSectionDropCompletedEvent.subscribe(handleDropComplete);
        return () => {
            TimelineSectionDropCompletedEvent.unsubscribe(handleDropComplete);
        };
    }, []);

    useEffect(() => {
        if (!dragPreviewData) {
            setDragOverTimeline(false);
        }
    }, [dragPreviewData]);

    useEffect(() => {
        if (!dragPreviewData) {
            return;
        }
        if (!draggingLibrarySections && !dragDropCompleted) {
            setShouldReturnPreview(true);
        } else if (draggingLibrarySections) {
            setShouldReturnPreview(false);
        }
    }, [draggingLibrarySections, dragPreviewData, dragDropCompleted]);

    useEffect(() => {
        if (dragDropCompleted) {
            setShouldReturnPreview(false);
        }
    }, [dragDropCompleted]);

    const deviceLayout = useDeviceLayout();

    const viewOnly = user !== null && project !== null && !project.userCanEdit(user);
    const renderModals = () => (
        <>
            {beginnerTourStartModalOpen && user && (
                <BeginnerTourStartModal
                    onClose={() => setBeginnerTourStartModalOpen(false)}
                    onStartTour={() => {
                        startBeginnerTour();
                        setBeginnerTourStartModalOpen(false);
                    }}
                    onCreateAccount={() => {
                        logout(); // This will redirect where users can create an account
                    }}
                    isGuestAccount={user?.roles.includes("Guest") ?? true}
                />
            )}
        </>
    );
    return (
        <>
            <LoadingScreen
                progress={loadingPercent}
                loadingAnimationComplete={() => {
                    setShowLoadingScreen(false);
                }}
                visible={showLoadingScreen}
            />

            {renderModals()}

            <DndContext
                sensors={sensors}
                onDragStart={handleDragStartFromDnd}
                onDragMove={handleDragMoveFromDnd}
                onDragOver={handleDragOverFromDnd}
                onDragEnd={handleDragEndFromDnd}
                onDragCancel={handleDragCancelFromDnd}
            >
                <AppShell
                    header={{ height: 40 }}
                    padding="md"
                    footer={{ height: deviceLayout.isPhoneOrSmaller ? 0 : 20 }}
                >
                    <AppShell.Header
                        // Prevent the key strokes from propagating to the Timeline where they will be consumed
                        onKeyDown={(event) => { event.stopPropagation(); }}
                        onKeyUp={(event) => { event.stopPropagation(); }}
                    >
                        <Header />
                    </AppShell.Header>

                    <AppShell.Main
                        hidden={user === null}
                        style={{
                            height: "1vh",
                            paddingTop: 40,
                            paddingBottom: deviceLayout.isPhoneOrSmaller ? 0 : 5,
                            paddingLeft: 0,
                            paddingRight: 0
                        }}
                    >
                        {user === null || !user.roles.includes("Instrumentalist") && (
                            /* Main content */
                            (project !== null) && (
                                <ResizablePane
                                    initialPercentage={deviceLayout.isTabletOrSmaller ? deviceLayout.isPhoneOrSmaller ? 28 : 34 : 40}
                                    minPercentage={10}
                                    maxPercentage={40}
                                    minPixelsForTop={100}
                                    maxPixelsForTop={1000}
                                    overrideBottom={viewOnly || deviceLayout.deviceLayout === DeviceLayout.PhoneLandscape}
                                >
                                    <Library
                                        ref={libraryRef}
                                        key="library"
                                        draggingSections={draggingLibrarySections}
                                        setDraggingSections={setDraggingLibrarySections}
                                    />
                                    <Timeline
                                        ref={timelineRef}
                                        key="timeline"
                                        draggingSections={draggingLibrarySections}
                                        setDraggingSections={setDraggingLibrarySections}
                                        setDragOverTimeline={setDragOverTimeline}
                                        updateProject={updateProject}
                                        project={project}
                                        viewOnly={viewOnly}
                                        beginnerTourActive={beginnerTourActive}
                                        currentTourStep={currentTourStep}
                                        moveToNextTourStep={moveToNextTourStep}
                                        moveToPrevTourStep={moveToPrevTourStep}
                                        jumpToTourStep={jumpToTourStep}
                                        loadingInstrument={loadingInstrument}
                                        onGhostItemLayoutChange={handleGhostLayoutChange}
                                        deviceLayout={deviceLayout}
                                    />
                                </ResizablePane>
                            )
                        )}
                    </AppShell.Main>

                    {!deviceLayout.isPhoneOrSmaller &&
                        <AppShell.Footer
                            px="xs"
                            py="xs"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Footer />
                        </AppShell.Footer>
                    }
                </AppShell >

                <DragPreviewOverlay
                    data={dragPreviewData}
                    cursor={dragCursor}
                    overTimeline={dragOverTimeline}
                    ghostLayouts={ghostLayouts}
                    shouldReturn={shouldReturnPreview}
                    dropCompleted={dragDropCompleted}
                    onReturnComplete={handlePreviewAnimationComplete}
                    onDropAnimationEnd={handlePreviewAnimationComplete}
                />
            </DndContext>
        </>
    );
};

export default ProjectPage;
